/**
 * PermissionMatrix - compact module x action grid for a single role.
 *
 * The "comparison" view of the permission editor: every module is a row and
 * the shared CRUD-style actions (View All / View Granted / Create / Edit All /
 * Edit Granted / Delete / Manage / Export) are columns, so the whole shape of a
 * role reads at a glance. Non-CRUD permissions (workflow actions, special
 * capabilities) do not fit the column scheme and are configured in the List
 * view - see `buildMatrixLayout` for how columns are derived.
 *
 * Cell state resolution and activation are owned by the caller (the editor
 * modal), so the matrix honours exactly the same grantable / override / locked
 * semantics as the list view.
 */

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { DataTable, type DataTableColumn } from "@/admin/components/data-table";
import {
  MATRIX_COLUMNS,
  type MatrixRow,
  type PermState,
} from "@/admin/lib/permissions";

interface PermissionMatrixProps {
  rows: MatrixRow[];
  grantedCodenames: Set<string>;
  /** Read-only (admin roles): every cell is shown, none is clickable. */
  readOnly: boolean;
  saving: boolean;
  /** Resolve how a codename behaves for this role. */
  stateForCodename: (codename: string) => PermState;
  /** Toggle a cell, or request the not-recommended override confirmation. */
  onActivate: (codename: string) => void;
}

function CheckMark() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path
        d="M2 5L4 7L8 3"
        stroke="#fff"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PermissionMatrix({
  rows,
  grantedCodenames,
  readOnly,
  saving,
  stateForCodename,
  onActivate,
}: PermissionMatrixProps) {
  const { t } = useTranslation();
  // Only render columns that at least one row populates.
  const visibleColumns = useMemo(() => {
    const active = new Set<string>();
    for (const r of rows) {
      for (const label of r.cellsByColumn.keys()) active.add(label);
    }
    return MATRIX_COLUMNS.filter((c) => active.has(c.label));
  }, [rows]);

  const columns = useMemo<DataTableColumn<MatrixRow>[]>(() => {
    const moduleColumn: DataTableColumn<MatrixRow> = {
      id: "module",
      header: t("roles.moduleHeader", "Module"),
      cell: (row) => (
        <span className="text-[12px] text-[var(--ink)]">{row.label}</span>
      ),
    };
    const actionColumns: DataTableColumn<MatrixRow>[] = visibleColumns.map(
      (c) => ({
        id: c.label,
        header: c.label,
        cell: (row) => {
          const cell = row.cellsByColumn.get(c.label);
          if (!cell) {
            return <span className="text-[10px] text-[var(--ink-3)]">-</span>;
          }
          const state = stateForCodename(cell.codename);
          const isGranted = grantedCodenames.has(cell.codename);
          const locked = state === "locked";
          const override = state === "override";
          const clickable = !readOnly && !locked && !saving;
          if (locked) {
            return (
              <span
                title={t("roles.cannotBeGrantedTooltip", {
                  codename: cell.codename,
                  defaultValue: "{{codename}} - cannot be granted to this role",
                })}
                className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-[3px] border-[0.5px] border-dashed border-[var(--ink-3)] bg-[var(--bg-subtle)] cursor-not-allowed"
              >
                <Lock className="h-2.5 w-2.5 text-[var(--ink-3)]" />
              </span>
            );
          }
          return (
            <Button
              type="button"
              variant="ghost"
              onClick={() => clickable && onActivate(cell.codename)}
              disabled={!clickable}
              title={cell.codename}
              className={cn(
                "inline-flex h-[18px] w-[18px] items-center justify-center rounded-[3px] border p-0 transition-all duration-150",
                isGranted && override
                  ? "border-[color:var(--amber-ink,#633806)] bg-[color:var(--amber-ink,#633806)]"
                  : isGranted
                    ? "border-[var(--green)] bg-[var(--green)]"
                    : override
                      ? "border-[color:var(--amber-ink,#633806)] bg-transparent"
                      : "border-[var(--rule-strong)] bg-transparent",
                clickable &&
                  !isGranted &&
                  !override &&
                  "cursor-pointer hover:border-[var(--green)]",
                clickable && "cursor-pointer",
                readOnly && "cursor-not-allowed opacity-60",
              )}
            >
              {isGranted && <CheckMark />}
            </Button>
          );
        },
      }),
    );
    return [moduleColumn, ...actionColumns];
  }, [
    visibleColumns,
    grantedCodenames,
    readOnly,
    saving,
    stateForCodename,
    onActivate,
    t,
  ]);

  return (
    <DataTable<MatrixRow>
      tableId="roles-permission-matrix"
      columns={columns}
      data={rows}
      rowKey="module"
    />
  );
}
